import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  getFullStyleSheetWithHighlight,
  getProductSpecsText,
  getStyleDetails,
  getBoxPdfDetailLines,
  getLabelPdfDetailLines,
  HEADER_IMAGE,
} from './quotePdfImages';
import { formatMoney, formatUnitPrice } from './buildPdfPayloadFromText';
import { formatShippingCell, hasShippingData, resolveShippingRowCosts } from './quotePdfPayloadUtils';
import { formatQuoteDateForPdf } from './quotePdfDateUtils';
import { getSectionOffset, scaleColumnStyles, tableMarginsFromLayout, tableStyleFromOffset, normalizeTableLayout } from './pdfLayoutUtils';

const DISCLAIMER = 'Above information is not an invoice and only an estimate of services/goods described above. Payment will be collected in prior to provision of services/goods described in this quote.';

const DEFAULT_SHIPPING_METHODS = [
  ['Premium Air shipping', '3-5 business days'],
  ['Express Air shipping', '4-5 business days'],
  ['Regular Air Shipping', '7-9 business days'],
  ['Express Sea shipping', '20-24 business days'],
  ['Regular Sea Shipping', '30-40 business days'],
];

const BAG_PRICING_HEAD_COLORS = [
  [0, 174, 239],
  [236, 0, 140],
  [255, 255, 255],
  [255, 242, 0],
  [0, 0, 0],
];

const BOX_PRICING_HEAD_COLORS = [
  [0, 174, 239],
  [236, 0, 140],
  [255, 105, 180],
  [180, 130, 70],
  [255, 255, 255],
  [0, 128, 0],
];

const LABEL_PRICING_HEAD_COLORS = [
  [0, 174, 239],
  [236, 0, 140],
  [186, 140, 200],
  [180, 130, 70],
  [255, 255, 255],
];

const IMAGE_LOAD_TIMEOUT_MS = 15000;

function loadImageDataUrl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      reject(new Error(`Timed out loading image: ${src}`));
    }, IMAGE_LOAD_TIMEOUT_MS);

    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL('image/jpeg', 0.92),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error(`Could not load image: ${src}`));
    };
    img.src = src;
  });
}

function fitImageBox(naturalW, naturalH, maxW, maxH) {
  const aspect = naturalW / naturalH;
  let width = maxW;
  let height = width / aspect;
  if (height > maxH) {
    height = maxH;
    width = height * aspect;
  }
  return { width, height };
}

function countQtyTiers(product) {
  if (product.pdfPricing?.length) return product.pdfPricing.length;
  const firstSku = product.skuDetails?.[0];
  const count = firstSku?.quantities?.filter((q) => q.value)?.length || 0;
  return Math.max(count, 1);
}

function getShippingCostsForTiers(product, tierCount) {
  const primary = product.pdfShipping?.[0];
  if (!primary) return Array.from({ length: tierCount }, () => '');
  return resolveShippingRowCosts(primary, tierCount);
}

function buildBagPricingRows(product) {
  const specs = getProductSpecsText(product);

  if (product.pdfPricing?.length) {
    return product.pdfPricing.map((row) => [
      specs,
      String(row.qty ?? ''),
      String(product.SKU || ''),
      row.pricePerUnit != null ? formatUnitPrice(row.pricePerUnit) : '',
      row.orderTotal != null ? `$${formatMoney(row.orderTotal)}` : '',
    ]);
  }

  const rows = [];
  (product.skuDetails || []).forEach((sku) => {
    (sku.quantities || []).forEach((qty) => {
      if (qty.value) {
        rows.push([
          specs,
          String(qty.value),
          String(sku.sku || product.SKU || ''),
          '',
          '',
        ]);
      }
    });
  });

  if (!rows.length) {
    rows.push([specs || '—', '—', String(product.SKU || '—'), '', '']);
  }
  return rows;
}

/** jsPDF built-in fonts lack ñ and accents — normalize to avoid garbled/overlapping glyphs. */
function sanitizePdfText(value) {
  if (value == null) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\u0020-\u007E]/g, (ch) => {
      const map = { '×': 'x', '—': '-', '–': '-', '°': ' deg' };
      return map[ch] || '';
    });
}

function buildLabelPricingBody(product) {
  const defaultSpecs = sanitizePdfText(getProductSpecsText(product));
  const pricingRows = product.pdfPricing || [];

  if (!pricingRows.length) {
    return [[defaultSpecs || '-', '-', '-', '', '']];
  }

  const body = [];
  let index = 0;

  while (index < pricingRows.length) {
    const specs = sanitizePdfText(pricingRows[index].specsLine || defaultSpecs);
    let span = 1;
    while (index + span < pricingRows.length) {
      const nextSpecs = sanitizePdfText(pricingRows[index + span].specsLine || defaultSpecs);
      if (nextSpecs !== specs) break;
      span += 1;
    }

    const first = pricingRows[index];
    body.push([
      span > 1
        ? { content: specs, rowSpan: span, styles: { valign: 'middle' } }
        : specs,
      String(first.qty ?? ''),
      sanitizePdfText(String(first.skuLabel ?? product.SKU ?? '')),
      first.pricePerUnit != null ? formatUnitPrice(first.pricePerUnit) : '',
      first.orderTotal != null ? `$${formatMoney(first.orderTotal)}` : '',
    ]);

    for (let offset = 1; offset < span; offset += 1) {
      const row = pricingRows[index + offset];
      body.push([
        String(row.qty ?? ''),
        sanitizePdfText(String(row.skuLabel ?? product.SKU ?? '')),
        row.pricePerUnit != null ? formatUnitPrice(row.pricePerUnit) : '',
        row.orderTotal != null ? `$${formatMoney(row.orderTotal)}` : '',
      ]);
    }

    index += span;
  }

  return body;
}

function buildBoxPricingRows(product) {
  const specs = getProductSpecsText(product);
  const autoCosts = getShippingCostsForTiers(product, countQtyTiers(product));

  if (product.pdfPricing?.length) {
    return product.pdfPricing.map((row, index) => [
      specs,
      String(row.qty ?? ''),
      String(product.SKU || ''),
      row.pricePerUnit != null ? formatUnitPrice(row.pricePerUnit) : '',
      row.orderTotal != null ? `$${formatMoney(row.orderTotal)}` : '',
      formatShippingCell(row.shippingCost ?? autoCosts[index] ?? ''),
    ]);
  }

  return [[specs || '—', '—', String(product.SKU || '—'), '', '', '']];
}

function shouldShowShippingTable(quoteData, product) {
  if (product?.showShippingTable === false) return false;
  if (product?.showShippingTable === true) return true;
  if (quoteData.showShippingTable === false) return false;
  return hasShippingData(product) || (product.pdfShipping || []).length > 0;
}

function drawShippingTableSection(pdf, product, quoteData, y, margin, pageWidth, contentRight) {
  const off = getSectionOffset(quoteData, 'shippingTable');
  const tableStyle = tableStyleFromOffset(off);
  const tierCount = countQtyTiers(product);
  const tableX = margin + off.x;
  const tableY = y + off.y;

  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(10);
  pdf.text('Shipping:', tableX, tableY);
  const startY = tableY + 6;

  const shippingHead = ['', ...Array.from({ length: tierCount }, () => ''), ''];
  const baseCol = { 0: { cellWidth: Math.max(60, 130 + Math.floor(off.w / 5)) } };
  pdf.autoTable({
    startY,
    head: [shippingHead],
    body: buildShippingRows(product, tierCount),
    theme: 'grid',
    styles: { fontSize: tableStyle.fontSize, cellPadding: tableStyle.cellPadding },
    headStyles: { fillColor: [255, 255, 255], textColor: [255, 255, 255], lineWidth: 0 },
    columnStyles: scaleColumnStyles(baseCol, off.w),
    margin: tableMarginsFromLayout(margin, pageWidth, contentRight, off),
  });
  return pdf.lastAutoTable.finalY + 10;
}

function drawTurnaroundText(pdf, quoteData, product, margin, contentBottom) {
  const text = (product?.pdfTurnaround || quoteData.pdfTurnaround || '').trim();
  if (!text) return;

  const lines = pdf.splitTextToSize(text, pdf.internal.pageSize.getWidth() - margin * 2);
  const lineHeight = 11;
  const startY = contentBottom - 42 - (lines.length - 1) * lineHeight;
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(236, 0, 140);
  pdf.text(lines, margin, startY);
  pdf.setTextColor(0, 0, 0);
}

function buildShippingRows(product, tierCount) {
  if (product.pdfShipping?.length) {
    return product.pdfShipping.map((row) => {
      const costs = resolveShippingRowCosts(row, tierCount);
      return [row.method, ...costs, row.timeframe || ''];
    });
  }

  const emptyTiers = Array.from({ length: tierCount }, () => '');
  return DEFAULT_SHIPPING_METHODS.map(([method, timeframe]) => [
    method,
    ...emptyTiers,
    timeframe,
  ]);
}

function paintPricingHeader(colors, blackTextCols = []) {
  return (data) => {
    if (data.section !== 'head' || data.column.index >= colors.length) return;
    const idx = data.column.index;
    data.cell.styles.fillColor = colors[idx];
    data.cell.styles.textColor = blackTextCols.includes(idx) ? [0, 0, 0] : [255, 255, 255];
    data.cell.styles.fontStyle = 'bold';
  };
}

function drawMetaBlock(pdf, lines, x, startY, maxWidth, lineHeight = 12) {
  let y = startY;
  lines.forEach((line) => {
    const wrapped = pdf.splitTextToSize(line, maxWidth);
    pdf.text(wrapped, x, y);
    y += wrapped.length * lineHeight;
  });
  return y;
}

function drawPageBorder(pdf, pageWidth, pageHeight) {
  const BORDER_INSET = 20;
  const borderW = pageWidth - BORDER_INSET * 2;
  const borderH = pageHeight - BORDER_INSET * 2;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(1.2);
  pdf.rect(BORDER_INSET, BORDER_INSET, borderW, borderH);
  return {
    BORDER_INSET,
    INNER_PAD: 14,
    contentLeft: BORDER_INSET + 14,
    contentRight: pageWidth - BORDER_INSET - 14,
    contentBottom: pageHeight - BORDER_INSET - 14,
    margin: BORDER_INSET + 14,
  };
}

async function drawHeaderLogo(pdf, margin, y, quoteData) {
  const logoOff = getSectionOffset(quoteData, 'logo');
  const x = margin + logoOff.x;
  const logoY = y + logoOff.y;
  try {
    const header = await loadImageDataUrl(HEADER_IMAGE);
    const { width: logoW, height: logoH } = fitImageBox(
      header.width,
      header.height,
      Math.max(80, 264 + logoOff.w),
      Math.max(40, 78 + logoOff.h),
    );
    pdf.addImage(header.dataUrl, 'JPEG', x, logoY, logoW, logoH);
    return logoH + 10 + logoOff.y;
  } catch {
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('LABELS LAB', x, logoY + 10);
    return 24 + logoOff.y;
  }
}

function drawDisclaimerFooter(pdf, layout, extraLines = []) {
  const { contentLeft, contentRight, contentBottom, margin } = layout;
  const disclaimerWidth = contentRight - contentLeft;
  const disclaimerLineHeight = 9;
  const disclaimerBottomPad = 14;

  let cursorY = contentBottom - disclaimerBottomPad;

  pdf.setFontSize(7.5);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(60, 60, 60);
  const disclaimerLines = pdf.splitTextToSize(DISCLAIMER, disclaimerWidth);
  cursorY -= (disclaimerLines.length - 1) * disclaimerLineHeight;
  pdf.text(disclaimerLines, (contentLeft + contentRight) / 2, cursorY, {
    align: 'center',
    maxWidth: disclaimerWidth,
  });

  if (extraLines.length) {
    cursorY -= 16;
    extraLines.forEach(({ text, color, size = 9, bold = false }) => {
      pdf.setFontSize(size);
      pdf.setFont(undefined, bold ? 'bold' : 'normal');
      pdf.setTextColor(...(color || [0, 0, 0]));
      pdf.text(text, margin, cursorY);
      cursorY -= 14;
    });
  }

  pdf.setTextColor(0, 0, 0);
}

function drawProductDescriptionSection(pdf, quoteData, product, y, margin, contentRight) {
  const off = getSectionOffset(quoteData, 'productDescription');
  const labelX = margin + off.x;
  let lineY = y + off.y;
  const valueMaxW = Math.max(80, contentRight - margin - 98 + off.w);
  const lineGap = 18 + Math.floor(off.h / 5);

  if (quoteData.projectId) {
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(10);
    pdf.text('Project ID:', labelX, lineY);
    pdf.setFont(undefined, 'normal');
    pdf.text(sanitizePdfText(quoteData.projectId), labelX + 98, lineY, { maxWidth: valueMaxW });
    lineY += lineGap;
  }

  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(10);
  pdf.text('Product description:', labelX, lineY);
  pdf.setFont(undefined, 'normal');
  const description = sanitizePdfText(
    quoteData.projectDescription || product.specialNotesToProduction || '—',
  );
  pdf.text(description, labelX + 98, lineY, { maxWidth: valueMaxW });
  return lineY + lineGap;
}

function drawPricingTable(pdf, quoteData, product, y, margin, pageWidth, contentRight, type) {
  const off = getSectionOffset(quoteData, 'pricingTable');
  const tableStyle = tableStyleFromOffset(off);
  const margins = tableMarginsFromLayout(margin, pageWidth, contentRight, off);
  const startY = y + off.y;

  const configs = {
    Bags: {
      head: [['Specs', 'QTY', 'SKU', 'PRICE PER UNIT', 'ORDER PRICE']],
      body: buildBagPricingRows(product),
      colors: BAG_PRICING_HEAD_COLORS,
      blackCols: [2, 3],
      columnStyles: {
        0: { cellWidth: 155 },
        1: { halign: 'center', cellWidth: 55 },
        2: { halign: 'center', cellWidth: 40 },
        3: { halign: 'center', cellWidth: 75 },
        4: { halign: 'center', cellWidth: 75 },
      },
    },
    Boxes: {
      head: [['Specs', 'QTY', 'SKU', 'PRICE PER UNIT', 'ORDER TOTAL', 'SHIPPING']],
      body: buildBoxPricingRows(product),
      colors: BOX_PRICING_HEAD_COLORS,
      blackCols: [4],
      columnStyles: {
        0: { cellWidth: 118 },
        1: { halign: 'center', cellWidth: 48 },
        2: { halign: 'center', cellWidth: 36 },
        3: { halign: 'center', cellWidth: 62 },
        4: { halign: 'center', cellWidth: 62 },
        5: { halign: 'center', cellWidth: 58 },
      },
    },
    Labels: {
      head: [['Specs', 'QTY', 'SKU', 'PRICE PER UNIT', 'ORDER PRICE']],
      body: buildLabelPricingBody(product),
      colors: LABEL_PRICING_HEAD_COLORS,
      blackCols: [4],
      columnStyles: {
        0: { cellWidth: 145, valign: 'middle' },
        1: { halign: 'center', cellWidth: 48 },
        2: { halign: 'center', cellWidth: 48 },
        3: { halign: 'center', cellWidth: 68 },
        4: { halign: 'center', cellWidth: 68 },
      },
      extraStyles: { valign: 'middle' },
    },
  };

  const cfg = configs[type] || configs.Bags;
  pdf.autoTable({
    startY,
    head: cfg.head,
    body: cfg.body,
    theme: 'grid',
    styles: {
      fontSize: tableStyle.fontSize,
      cellPadding: tableStyle.cellPadding,
      lineColor: [200, 200, 200],
      lineWidth: 0.5,
      ...(cfg.extraStyles || {}),
    },
    headStyles: { fontStyle: 'bold' },
    columnStyles: scaleColumnStyles(cfg.columnStyles, off.w),
    margin: margins,
    didParseCell: paintPricingHeader(cfg.colors, cfg.blackCols),
  });
  return pdf.lastAutoTable.finalY + 10;
}

function drawCustomTablesSection(pdf, quoteData, y, margin, pageWidth, contentRight, contentBottom) {
  const tables = quoteData.pdfCustomTables || [];
  if (!tables.length) return y;

  const sectionOff = getSectionOffset(quoteData, 'customTables');

  tables.forEach((table) => {
    const cells = (table.cells || []).map((row) =>
      row.map((cell) => sanitizePdfText(cell ?? '')),
    );
    if (!cells.length || !cells.some((row) => row.some((cell) => cell.trim()))) return;

    const tableOff = normalizeTableLayout(table.layout);
    const combinedOff = {
      x: sectionOff.x + tableOff.x,
      y: sectionOff.y + tableOff.y,
      w: sectionOff.w + tableOff.w,
      h: sectionOff.h + tableOff.h,
    };
    const tableStyle = tableStyleFromOffset(combinedOff);
    const tableX = margin + combinedOff.x;
    let tableY = y + combinedOff.y;

    const estimatedHeight = (cells.length + (table.title?.trim() ? 1 : 0)) * 18 + 24;
    if (tableY + estimatedHeight > contentBottom - 60) {
      pdf.addPage();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const layout = drawPageBorder(pdf, pageWidth, pageHeight);
      tableY = layout.margin + combinedOff.y;
    }

    if (table.title?.trim()) {
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text(sanitizePdfText(table.title.trim()), tableX, tableY);
      tableY += 12;
    }

    const useHeader = table.headerRow !== false && cells.length > 0;
    const head = useHeader ? [cells[0]] : undefined;
    const body = useHeader ? cells.slice(1) : cells;
    const colCount = cells[0]?.length || 1;
    const baseColWidth = Math.max(40, Math.floor(400 / colCount));
    const columnStyles = scaleColumnStyles(
      Object.fromEntries(Array.from({ length: colCount }, (_, i) => [i, { cellWidth: baseColWidth }])),
      combinedOff.w,
    );

    pdf.autoTable({
      startY: tableY,
      head,
      body: body.length ? body : cells,
      theme: 'grid',
      styles: {
        fontSize: tableStyle.fontSize,
        cellPadding: tableStyle.cellPadding,
        lineColor: [200, 200, 200],
        lineWidth: 0.5,
      },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      columnStyles,
      margin: tableMarginsFromLayout(margin, pageWidth, contentRight, combinedOff),
    });
    y = pdf.lastAutoTable.finalY + 12;
  });

  return y;
}

async function renderBagProductPage(pdf, quoteData, product, productIndex) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const layout = drawPageBorder(pdf, pageWidth, pageHeight);

  const { contentLeft, contentRight, contentBottom, margin } = layout;
  let y = margin;

  y += await drawHeaderLogo(pdf, margin, y, quoteData);

  const contentWidth = contentRight - contentLeft;
  const metaOff = getSectionOffset(quoteData, 'meta');
  const metaWidth = Math.max(80, 145 + metaOff.w);
  const metaLineHeight = 12 + Math.floor(metaOff.h / 10);
  const metaX = contentRight - metaWidth - contentWidth * 0.2 + metaOff.x;
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  drawMetaBlock(pdf, [
    `Date: ${formatQuoteDateForPdf(quoteData.quoteDate)}`,
    `Client: ${quoteData.companyName || ''}`,
    `Attention to: ${quoteData.attentionTo || quoteData.contactName || ''}`,
    `Prepared by: ${quoteData.salesRepName || ''}`,
    `Valid Until: ${formatQuoteDateForPdf(quoteData.validUntil)}`,
  ], metaX, margin + 4 + metaOff.y, metaWidth, metaLineHeight);

  const styleSheet = await getFullStyleSheetWithHighlight(product);
  const details = getStyleDetails(product);
  const sheetOff = getSectionOffset(quoteData, 'styleSheet');
  const detailsOff = getSectionOffset(quoteData, 'details');
  const sheetW = Math.max(120, 250 + sheetOff.w);
  const sheetH = styleSheet ? Math.max(60, 115 + sheetOff.h) : 0;
  const detailsX = margin + sheetW + 14 + detailsOff.x;
  const detailLineHeight = 14 + Math.floor(detailsOff.h / 10);
  const detailValueMaxW = Math.max(40, contentRight - detailsX - 72 + detailsOff.w);

  if (styleSheet) {
    pdf.addImage(styleSheet, 'JPEG', margin + sheetOff.x, y + sheetOff.y, sheetW, sheetH);
  }

  let detailY = y + 4 + detailsOff.y;
  pdf.setFontSize(10);
  [
    [`${details.stylePrefix}:`, `#${details.styleNumber}`],
    ['Material:', details.material || '—'],
    ['Loading side:', details.loadingSide || '—'],
    ['Zipper:', details.zipper || '—'],
    ['Finish:', details.finish || '—'],
  ].forEach(([label, value]) => {
    pdf.setFont(undefined, 'bold');
    pdf.text(label, detailsX, detailY);
    pdf.setFont(undefined, 'normal');
    pdf.text(String(value), detailsX + 72, detailY, { maxWidth: detailValueMaxW });
    detailY += detailLineHeight;
  });

  y += sheetH + 12;

  y = drawProductDescriptionSection(pdf, quoteData, product, y, margin, contentRight);
  y = drawPricingTable(pdf, quoteData, product, y, margin, pageWidth, contentRight, 'Bags');

  if (shouldShowShippingTable(quoteData, product)) {
    y = drawShippingTableSection(pdf, product, quoteData, y, margin, pageWidth, contentRight);
  } else {
    drawTurnaroundText(pdf, quoteData, product, margin, contentBottom);
  }

  y = drawCustomTablesSection(pdf, quoteData, y, margin, pageWidth, contentRight, contentBottom);

  drawDisclaimerFooter(pdf, { contentLeft, contentRight, contentBottom, margin });
}

async function renderBoxProductPage(pdf, quoteData, product, productIndex) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const layout = drawPageBorder(pdf, pageWidth, pageHeight);

  const { contentLeft, contentRight, contentBottom, margin } = layout;
  let y = margin;

  y += await drawHeaderLogo(pdf, margin, y, quoteData);

  const contentWidth = contentRight - contentLeft;
  const metaOff = getSectionOffset(quoteData, 'meta');
  const metaWidth = Math.max(80, 145 + metaOff.w);
  const metaLineHeight = 12 + Math.floor(metaOff.h / 10);
  const metaX = contentRight - metaWidth - contentWidth * 0.2 + metaOff.x;
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  drawMetaBlock(pdf, [
    `Date: ${formatQuoteDateForPdf(quoteData.quoteDate)}`,
    `Client: ${quoteData.companyName || ''}`,
    `Attention to: ${quoteData.attentionTo || quoteData.contactName || ''}`,
    `Prepared by: ${quoteData.salesRepName || ''}`,
  ], metaX, margin + 4 + metaOff.y, metaWidth, metaLineHeight);

  const styleSheet = await getFullStyleSheetWithHighlight(product);
  const sheetOff = getSectionOffset(quoteData, 'styleSheet');
  const detailsOff = getSectionOffset(quoteData, 'details');
  const sheetW = Math.max(140, 270 + sheetOff.w);
  const sheetH = styleSheet ? Math.max(80, 168 + sheetOff.h) : 0;
  const detailsX = margin + sheetW + 14 + detailsOff.x;
  const detailLineHeight = 14 + Math.floor(detailsOff.h / 10);
  const detailValueMaxW = Math.max(40, contentRight - detailsX - 88 + detailsOff.w);

  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(10);
  pdf.text('Box Styles', margin + sheetOff.x, y + sheetOff.y);
  y += 12;

  if (styleSheet) {
    pdf.addImage(styleSheet, 'JPEG', margin + sheetOff.x, y + sheetOff.y, sheetW, sheetH);
  }

  let detailY = y + 4 + detailsOff.y;
  pdf.setFontSize(10);
  getBoxPdfDetailLines(product).forEach(([label, value]) => {
    pdf.setFont(undefined, 'bold');
    pdf.text(label, detailsX, detailY);
    pdf.setFont(undefined, 'normal');
    pdf.text(String(value || ''), detailsX + 88, detailY, { maxWidth: detailValueMaxW });
    detailY += detailLineHeight;
  });

  y += sheetH + 12;

  y = drawProductDescriptionSection(pdf, quoteData, product, y, margin, contentRight);
  y = drawPricingTable(pdf, quoteData, product, y, margin, pageWidth, contentRight, 'Boxes');

  if (shouldShowShippingTable(quoteData, product)) {
    y = drawShippingTableSection(pdf, product, quoteData, y, margin, pageWidth, contentRight);
  } else {
    drawTurnaroundText(pdf, quoteData, product, margin, contentBottom);
  }

  y = drawCustomTablesSection(pdf, quoteData, y, margin, pageWidth, contentRight, contentBottom);

  drawDisclaimerFooter(pdf, { contentLeft, contentRight, contentBottom, margin });
}

async function renderLabelProductPage(pdf, quoteData, product, productIndex) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const layout = drawPageBorder(pdf, pageWidth, pageHeight);

  const { contentLeft, contentRight, contentBottom, margin } = layout;
  let y = margin;

  y += await drawHeaderLogo(pdf, margin, y, quoteData);

  const contentWidth = contentRight - contentLeft;
  const metaOff = getSectionOffset(quoteData, 'meta');
  const metaWidth = Math.max(80, 145 + metaOff.w);
  const metaLineHeight = 12 + Math.floor(metaOff.h / 10);
  const metaX = contentRight - metaWidth - contentWidth * 0.2 + metaOff.x;
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  drawMetaBlock(pdf, [
    `Date: ${formatQuoteDateForPdf(quoteData.quoteDate, { longYear: true })}`,
    `Client: ${quoteData.companyName || ''}`,
    `Attention to: ${quoteData.attentionTo || quoteData.contactName || ''}`,
    `Prepared by: ${quoteData.salesRepName || ''}`,
    `Valid Until: ${formatQuoteDateForPdf(quoteData.validUntil, { longYear: true })}`,
  ], metaX, margin + 4 + metaOff.y, metaWidth, metaLineHeight);

  const styleSheet = await getFullStyleSheetWithHighlight(product);
  const sheetOff = getSectionOffset(quoteData, 'styleSheet');
  const detailsOff = getSectionOffset(quoteData, 'details');
  const sheetW = Math.max(120, 230 + sheetOff.w);
  const sheetH = styleSheet ? Math.max(60, 105 + sheetOff.h) : 0;
  const detailsX = margin + sheetW + 14 + detailsOff.x;
  const detailLineHeight = 14 + Math.floor(detailsOff.h / 10);
  const detailValueMaxW = Math.max(40, contentRight - detailsX - 88 + detailsOff.w);

  if (styleSheet) {
    pdf.addImage(styleSheet, 'JPEG', margin + sheetOff.x, y + sheetOff.y, sheetW, sheetH);
  }

  let detailY = y + 4 + detailsOff.y;
  pdf.setFontSize(10);
  getLabelPdfDetailLines(product).forEach(([label, value]) => {
    pdf.setFont(undefined, 'bold');
    pdf.text(label, detailsX, detailY);
    pdf.setFont(undefined, 'normal');
    pdf.text(sanitizePdfText(String(value || '')), detailsX + 88, detailY, { maxWidth: detailValueMaxW });
    detailY += detailLineHeight;
  });

  y += sheetH + 12;

  y = drawProductDescriptionSection(pdf, quoteData, product, y, margin, contentRight);
  y = drawPricingTable(pdf, quoteData, product, y, margin, pageWidth, contentRight, 'Labels');
  y = drawCustomTablesSection(pdf, quoteData, y, margin, pageWidth, contentRight, contentBottom);

  const leadTime = sanitizePdfText((product.pdfOrderLeadTime || quoteData.pdfOrderLeadTime || '').trim());
  if (leadTime) {
    const leadY = contentBottom - 42;
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(236, 0, 140);
    pdf.text(leadTime, margin, leadY);
    pdf.setTextColor(0, 0, 0);
  }

  drawDisclaimerFooter(pdf, { contentLeft, contentRight, contentBottom, margin });
}

async function renderProductPage(pdf, quoteData, product, productIndex) {
  if (productIndex > 0) {
    pdf.addPage();
  }

  if (product.productType === 'Boxes') {
    await renderBoxProductPage(pdf, quoteData, product, productIndex);
  } else if (product.productType === 'Labels') {
    await renderLabelProductPage(pdf, quoteData, product, productIndex);
  } else {
    await renderBagProductPage(pdf, quoteData, product, productIndex);
  }
}

function validateQuoteData(quoteData) {
  if (!quoteData?.companyName?.trim()) {
    throw new Error('Client / company name is required.');
  }
  const products = (quoteData.products || []).filter((p) => p.productType);
  if (!products.length) {
    throw new Error('Could not detect a product type from the pasted text.');
  }
  return products;
}

export async function buildLabelsLabQuotePdfBlob(quoteData, { productIndices } = {}) {
  const products = validateQuoteData(quoteData);
  const indices = productIndices ?? products.map((_, i) => i);
  const pdf = new jsPDF('p', 'pt', 'letter');

  let pageIndex = 0;
  for (let i = 0; i < indices.length; i += 1) {
    const product = products[indices[i]];
    if (!product) continue;
    await renderProductPage(pdf, quoteData, product, pageIndex);
    pageIndex += 1;
  }

  return pdf.output('blob');
}

export async function generateLabelsLabQuotePdf(quoteData, { fileName, productIndices } = {}) {
  const blob = await buildLabelsLabQuotePdfBlob(quoteData, { productIndices });
  const safeName = (quoteData.projectId || quoteData.companyName || 'quote')
    .replace(/[^\w-]+/g, '_');
  const name = fileName || `LabelsLab-Quote-${safeName}.pdf`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

export async function downloadLabelsLabQuotePdfs(quoteData) {
  const products = validateQuoteData(quoteData);
  const safeName = (quoteData.projectId || quoteData.companyName || 'quote')
    .replace(/[^\w-]+/g, '_');
  const mode = quoteData.pdfOutputMode || 'combined';

  if (mode === 'combined' || products.length === 1) {
    await generateLabelsLabQuotePdf(quoteData);
    return;
  }

  for (let i = 0; i < products.length; i += 1) {
    const type = (products[i].productType || 'product').replace(/\s+/g, '-');
    const sameTypeCount = products.filter((p) => p.productType === products[i].productType).length;
    const typeIndex = products.slice(0, i + 1).filter((p) => p.productType === products[i].productType).length;
    const suffix = sameTypeCount > 1 ? `-${typeIndex}` : '';
    await generateLabelsLabQuotePdf(quoteData, {
      productIndices: [i],
      fileName: `LabelsLab-Quote-${safeName}-${type}${suffix}.pdf`,
    });
  }
}
